import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [company, setCompany] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isFetchingRef = useRef(false);
    const isInitializedRef = useRef(false);

    // Fetch user profile and company data
    const fetchUserProfile = async (userId) => {
        if (isFetchingRef.current) {
            return null;
        }

        isFetchingRef.current = true;

        try {
            const { data: profile, error: profileError } = await supabase
                .from('users')
                .select(`
          *,
          companies (
            id,
            name,
            slug,
            logo_url,
            primary_color,
            secondary_color,
            timezone,
            settings
          )
        `)
                .eq('id', userId)
                .single();

            if (profileError) {
                console.error('Profile fetch error:', profileError);
                setError(`Failed to fetch profile: ${profileError.message}`);
                return null;
            }

            if (!profile) {
                console.error('No profile data returned');
                setError('No profile found for user');
                return null;
            }

            setUserProfile(profile);
            setCompany(profile.companies);
            setError(null);
            return profile;
        } catch (error) {
            console.error('❌ Unexpected error fetching user profile:', error);
            setError(error.message);
            return null;
        } finally {
            isFetchingRef.current = false;
        }
    };

    // Initialize auth state
    useEffect(() => {
        let mounted = true;

        const initializeAuth = async () => {
            try {

                const { data: { session }, error: sessionError } = await supabase.auth.getSession();

                if (sessionError) {
                    console.error('Session error:', sessionError);
                    throw sessionError;
                }


                if (!mounted) return;

                setSession(session);
                setUser(session?.user ?? null);

                if (session?.user) {
                    await fetchUserProfile(session.user.id);
                }

                setLoading(false);
            } catch (error) {
                console.error('Auth initialization error:', error);
                setError(error.message);
            } finally {
                if (mounted) {
                    isInitializedRef.current = true;
                    setLoading(false);
                }
            }
        };

        initializeAuth();

        // Listen for auth changes - NO ASYNC operations in callback
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, session) => {

                if (!mounted) return;

                // 🆕 Skip state updates if we're still in initial loading
                if (!isInitializedRef.current) {
                    return;
                }

                setSession(session);
                setUser(session?.user ?? null);

                // Use setTimeout to defer async operations outside the callback
                setTimeout(() => {
                    if (!mounted) return;

                    if (session?.user) {
                        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
                            fetchUserProfile(session.user.id);
                        } else if (event === 'TOKEN_REFRESHED') {
                            // console.log('Token refreshed - keeping existing profile');
                            // Don't refetch on token refresh, profile should already exist
                        } else if (event === 'INITIAL_SESSION') {
                            // console.log('Initial session - profile already fetched in init');
                        }
                    } else if (event === 'SIGNED_OUT') {
                        setUserProfile(null);
                        setCompany(null);
                    }
                }, 0);
            }
        );

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, []);

    // Sign in with email and password
    const signIn = async (email, password) => {
        try {
            setError(null);

            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) {
                console.error('Sign in error:', error);
                throw error;
            }

            // Profile will be fetched by onAuthStateChange SIGNED_IN event
            return { data, error: null };
        } catch (error) {
            console.error('❌ Sign in failed:', error);
            setError(error.message);
            return { data: null, error };
        }
    };

    // Sign out
    const signOut = async () => {
        try {
            const { error } = await supabase.auth.signOut();
            if (error) throw error;

            setUser(null);
            setUserProfile(null);
            setCompany(null);
            setSession(null);
            setError(null);

            return { error: null };
        } catch (error) {
            console.error('❌ Sign out error:', error);
            return { error };
        }
    };

    // Refresh user profile
    const refreshProfile = async () => {
        if (user) {
            return await fetchUserProfile(user.id);
        }
        return null;
    };

    const value = {
        user,
        userProfile,
        company,
        session,
        loading,
        error,
        signIn,
        signOut,
        refreshProfile,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};